import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isFieldForCategory, isFieldForListingType } from '../../../util/fieldHelpers';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';
import { updateRequest } from '../../../util/api';

import {
  CustomExtendedDataField,
  FieldTextInput,
  Form,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';

import css from './RequestEditForm.module.css';

const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_DESCRIPTION = 5000;

/**
 * The multiple-choice questions that apply to a request's category: the same ones the
 * request form asked. Free-text fields are left out (the server only accepts option ids
 * in public data, and the homeowner's own words belong in the private description).
 *
 * @param {Array<Object>} listingFieldConfigs config.listing.listingFields
 * @param {string} category the request's category id
 * @returns {Array<Object>}
 */
export const editableFieldConfigs = (listingFieldConfigs, category) =>
  (listingFieldConfigs || []).filter(
    config =>
      config.scope === 'public' &&
      [SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(config.schemaType) &&
      isFieldForListingType('request', config) &&
      isFieldForCategory([category], config)
  );

/**
 * Lets a homeowner edit their own request: description (private), multiple-choice
 * answers (public, as on the request form) and photos (private; remove existing ones,
 * add new ones, up to 10). Saved through /api/request-update.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing the request listing
 * @param {Object} props.details { description, photos: [{ id, url }] } from /api/request-details
 * @param {Array<Object>} props.listingFieldConfigs config.listing.listingFields
 * @param {Function} props.onSaved called after a successful save
 * @param {Function} props.onCancel
 * @returns {JSX.Element}
 */
const RequestEditForm = props => {
  const { listing, details, listingFieldConfigs, onSaved, onCancel, className } = props;
  const intl = useIntl();
  const publicData = listing?.attributes?.publicData || {};
  const fieldConfigs = editableFieldConfigs(listingFieldConfigs, publicData.category);
  const photos = details?.photos || [];

  const [removeIds, setRemoveIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [photoError, setPhotoError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const keptCount = photos.filter(p => !removeIds.includes(p.id)).length;

  const toggleRemove = id =>
    setRemoveIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));

  const addFiles = fileList => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (files.some(f => f.size > MAX_PHOTO_BYTES)) {
      setPhotoError('RequestEditForm.photoTooLarge');
      return;
    }
    if (keptCount + newFiles.length + files.length > MAX_PHOTOS) {
      setPhotoError('RequestEditForm.tooManyPhotos');
      return;
    }
    setPhotoError(null);
    setNewFiles(current => [...current, ...files]);
  };

  const initialValues = {
    projectDescription: details?.description || '',
    ...fieldConfigs.reduce(
      (acc, c) =>
        publicData[c.key] !== undefined ? { ...acc, [`pub_${c.key}`]: publicData[c.key] } : acc,
      {}
    ),
  };

  const onSubmit = values => {
    const formData = new FormData();
    formData.append('requestId', listing.id.uuid);
    formData.append('projectDescription', values.projectDescription || '');
    const answers = fieldConfigs.reduce((acc, c) => {
      const v = values[`pub_${c.key}`];
      return v === undefined || v === null || v === '' ? acc : { ...acc, [`pub_${c.key}`]: v };
    }, {});
    formData.append('answers', JSON.stringify(answers));
    formData.append('removePhotoIds', JSON.stringify(removeIds));
    // Only what the server needs to label the title.
    formData.append(
      'listingFieldConfigs',
      JSON.stringify(fieldConfigs.map(({ key, enumOptions }) => ({ key, enumOptions })))
    );
    newFiles.forEach(f => formData.append('images', f, f.name));

    setSaveError(null);
    return updateRequest(formData)
      .then(() => onSaved())
      .catch(e =>
        setSaveError(
          e?.data?.error === 'too-many-photos'
            ? 'RequestEditForm.tooManyPhotos'
            : 'RequestEditForm.saveFailed'
        )
      );
  };

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting }) => (
        <Form className={classNames(css.root, className)} onSubmit={handleSubmit}>
          <FieldTextInput
            id="requestEdit.projectDescription"
            name="projectDescription"
            type="textarea"
            maxLength={MAX_DESCRIPTION}
            label={intl.formatMessage({ id: 'RequestEditForm.descriptionLabel' })}
            className={css.field}
          />

          {fieldConfigs.map(fieldConfig => (
            <CustomExtendedDataField
              key={fieldConfig.key}
              name={`pub_${fieldConfig.key}`}
              fieldConfig={fieldConfig}
              defaultRequiredMessage={intl.formatMessage({
                id: 'RequestEditForm.requiredMessage',
              })}
              formId="requestEdit"
              className={css.field}
            />
          ))}

          <fieldset className={css.photos}>
            <legend className={css.legend}>
              <FormattedMessage id="RequestEditForm.photosLabel" />
            </legend>
            <ul className={css.photoGrid}>
              {photos.map((photo, i) => {
                const removing = removeIds.includes(photo.id);
                const checkboxId = `requestEdit.remove.${i}`;
                return (
                  <li
                    key={photo.id}
                    className={classNames(css.photo, { [css.removing]: removing })}
                  >
                    <img
                      src={photo.url}
                      alt={intl.formatMessage(
                        { id: 'RequestEditForm.photoAlt' },
                        { number: i + 1, count: photos.length }
                      )}
                      className={css.photoImg}
                    />
                    <label htmlFor={checkboxId} className={css.removeLabel}>
                      <input
                        id={checkboxId}
                        type="checkbox"
                        checked={removing}
                        onChange={() => toggleRemove(photo.id)}
                        aria-label={intl.formatMessage(
                          { id: 'RequestEditForm.removePhoto' },
                          { number: i + 1 }
                        )}
                      />
                      <FormattedMessage id="RequestEditForm.remove" />
                    </label>
                  </li>
                );
              })}
            </ul>
            {newFiles.length > 0 ? (
              <ul className={css.newFiles}>
                {newFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    {f.name}{' '}
                    <button
                      type="button"
                      className={css.linkButton}
                      onClick={() => setNewFiles(files => files.filter((_, j) => j !== i))}
                    >
                      <FormattedMessage id="RequestEditForm.undoAdd" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <label htmlFor="requestEdit.addPhotos" className={css.addLabel}>
              <FormattedMessage id="RequestEditForm.addPhotos" />
            </label>
            <input
              id="requestEdit.addPhotos"
              type="file"
              accept="image/*"
              multiple
              className={css.fileInput}
              onChange={e => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <p className={css.hint}>
              <FormattedMessage
                id="RequestEditForm.photoCount"
                values={{ count: keptCount + newFiles.length, max: MAX_PHOTOS }}
              />
            </p>
            {photoError ? (
              <p className={css.error} role="alert">
                <FormattedMessage id={photoError} />
              </p>
            ) : null}
          </fieldset>

          {saveError ? (
            <p className={css.error} role="alert">
              <FormattedMessage id={saveError} />
            </p>
          ) : null}

          <div className={css.actions}>
            <PrimaryButton type="submit" inProgress={submitting} disabled={submitting}>
              <FormattedMessage id="RequestEditForm.save" />
            </PrimaryButton>
            <SecondaryButton type="button" onClick={onCancel} disabled={submitting}>
              <FormattedMessage id="RequestEditForm.cancel" />
            </SecondaryButton>
          </div>
        </Form>
      )}
    />
  );
};

export default RequestEditForm;
